import { IMeasurement } from "../../../../@types/admin/AdminMeasurement";
import { useMeasurementTypeValidations } from "../../../../hooks/queries/admin/measurements";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { summariseMeasurementValues } from "../../Patients/measurementEdit";
import {
  ModalContainer,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  StatusBadge,
} from "../AdminPage.styles";
import {
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  ValueGrid,
} from "./PatientDetailsPanel.styles";

interface IMeasurementDetailModalProps {
  measurement: IMeasurement;
}

/**
 * One measurement's recorded values (EN-5169) — Retool's `modalMeasurement`.
 *
 * Retool showed a raw JSON explorer over the `data` column its query built,
 * so the operator read `{"bp_systolic": 128, "bp_diastolic": 74}` and had to
 * know the column names. The same information is rendered as labelled values,
 * reusing `summariseMeasurementValues` from the Measurements page — the labels
 * come from the measurement-type catalogue, which is where the display names
 * live.
 *
 * This is also why the endpoint needed no jsonb: `measurementTypes` already
 * names the columns the row has values in, which is the only thing the jsonb
 * was for.
 */
export function MeasurementDetailModal({
  measurement,
}: IMeasurementDetailModalProps) {
  const { measurementTypes } = useMeasurementTypeValidations();
  const values = summariseMeasurementValues(measurement, measurementTypes);

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>Measurement details</ModalTitle>
        <ModalSubtitle>
          Measurement ID {measurement.id} ·{" "}
          {getLongFormattedDate(measurement.time, "no time recorded")}
          {measurement.source ? ` · ${measurement.source}` : ""}
        </ModalSubtitle>
      </ModalHeader>

      {measurement.isArchived && (
        <StatusBadge status="inactive">Archived</StatusBadge>
      )}

      {values.length === 0 ? (
        <SummaryValue>This measurement carries no values.</SummaryValue>
      ) : (
        <ValueGrid>
          {values.map((entry) => (
            <SummaryItem key={entry.label}>
              <SummaryLabel>{entry.label}</SummaryLabel>
              <SummaryValue>{entry.value}</SummaryValue>
            </SummaryItem>
          ))}
        </ValueGrid>
      )}
    </ModalContainer>
  );
}
